import { Link } from '@tanstack/react-router'
import { ShieldAlert, Sparkles } from 'lucide-react'
import { useState } from 'react'

import {
  usePostAction,
  usePreviewPosts,
  type AutomationOut,
  type PostOut,
  type PostPreviewOut,
  type StudioSessionOut,
} from '@/api/endpoints/engagement'
import { Button } from '@/components/ui/Button'
import { Chip, type ChipVariant } from '@/components/ui/Chip'
import { TimeInput } from '@/components/ui/Input'
import { Segmented } from '@/components/ui/Segmented'
import { Skeleton, Spinner } from '@/components/ui/Spinner'
import { Textarea } from '@/components/ui/Textarea'
import { Toggle } from '@/components/ui/Toggle'
import { errorMessage } from '@/lib/errors'
import { cn } from '@/lib/cn'
import {
  clockLabel,
  dayLabel,
  POST_TYPE_INFO,
  POST_TYPES,
  PRIORITY_LABELS,
  postsPerWeekOf,
  typeWeights,
  type EngagementValues,
  type Priority,
} from './model'
import { AutomationNotice, SessionNotice } from './notices'
import { DayPicker, Field, Help, Section } from './parts'
import styles from './ChannelEditDialog.module.scss'

export interface PostsTabProps {
  slug: string
  values: EngagementValues
  errors: Record<string, string>
  set: <K extends keyof EngagementValues>(key: K, value: EngagementValues[K]) => void
  before: (key: keyof EngagementValues) => string | undefined
  automation: AutomationOut | undefined
  session: StudioSessionOut | undefined
  posts: PostOut[] | undefined
  postsLoading: boolean
  queueRef: (el: HTMLElement | null) => void
}

const STATUS: Record<PostOut['status'], { label: string; variant: ChipVariant }> = {
  draft: { label: 'Rascunho', variant: 'default' },
  awaiting_video: { label: 'Rascunho', variant: 'default' },
  queued: { label: 'Na fila', variant: 'warning' },
  rescheduled: { label: 'Reprogramado', variant: 'warning' },
  scheduled: { label: 'Programado', variant: 'primary' },
  published: { label: 'Publicado', variant: 'success' },
  expired: { label: 'Expirado', variant: 'warning' },
  rejected: { label: 'Reprovado na segurança', variant: 'danger' },
  failed: { label: 'Falhou', variant: 'danger' },
}

function whenLabel(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  const today = new Date()
  const sameDay =
    d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate()
  return `${sameDay ? 'hoje' : dayLabel(iso)} · ${clockLabel(iso)}`
}

export function PostsTab(props: PostsTabProps) {
  const { values, errors, set, before } = props
  const enabledTypes = POST_TYPES.filter((t) => values.posts_types[t].enabled).length
  const dayCount = values.posts_days.length

  return (
    <div className={styles.tabPanel}>
      <SessionNotice session={props.session} />
      {props.automation && !props.automation.posts_enabled ? (
        <div className={styles.section}>
          <AutomationNotice kind="posts" />
        </div>
      ) : null}

      <Section>
        <div className={styles.toggleHead}>
          <Toggle
            id="ch-posts-enabled"
            aria-label="Posts automáticos"
            checked={values.posts_enabled}
            onCheckedChange={(v) => {
              set('posts_enabled', v)
            }}
          />
          <div className={styles.toggleHeadText}>
            <h3 className={styles.sectionTitle}>Posts automáticos</h3>
            <span className={styles.hint}>O robô escreve o texto e programa o post na aba Comunidade do canal.</span>
          </div>
        </div>
      </Section>

      <Section title="Frequência">
        <Field
          label="Horário"
          help="O melhor horário é o pico de audiência que o ciclo de upload calcula para o canal. Sem dados de pico, 18:00."
          before={before('posts_time_mode') ?? before('posts_time')}
          error={errors.posts_time}
        >
          <Segmented
            aria-label="Horário dos posts"
            options={[
              { value: 'best', label: 'Melhor horário do canal' },
              { value: 'fixed', label: 'Horário fixo' },
            ]}
            value={values.posts_time_mode}
            onValueChange={(v) => {
              set('posts_time_mode', v)
            }}
          />
          {values.posts_time_mode === 'fixed' ? (
            <TimeInput
              id="ch-posts-time"
              aria-label="Horário fixo dos posts"
              step={900}
              value={values.posts_time}
              onChange={(v) => {
                set('posts_time', v)
              }}
              error={Boolean(errors.posts_time)}
            />
          ) : null}
        </Field>
        <Field
          groupId="ch-posts-days"
          label="Dias da semana"
          meta={`${String(dayCount)} ${dayCount === 1 ? 'dia' : 'dias'} · ${String(dayCount)} ${dayCount === 1 ? 'post' : 'posts'} por semana`}
          before={before('posts_days')}
          error={errors.posts_days}
          hint="Cada dia marcado recebe 1 post."
        >
          <DayPicker
            id="ch-posts-days"
            value={values.posts_days}
            onChange={(days) => {
              set('posts_days', days)
            }}
          />
        </Field>
      </Section>

      <Section
        title={
          <span className={styles.labelRow}>
            Mistura de tipos
            <Help label="prioridade dos tipos">Alta sai mais vezes na mistura da semana; Baixa, menos.</Help>
          </span>
        }
        meta={`${String(enabledTypes)} de ${String(POST_TYPES.length)} ligados`}
      >
        <div id="ch-posts-types">
          {POST_TYPES.map((type) => {
            const info = POST_TYPE_INFO[type]
            const entry = values.posts_types[type]
            return (
              <div key={type} className={cn(styles.typeRow, !entry.enabled && styles.typeOff)}>
                <Toggle
                  aria-label={info.label}
                  checked={entry.enabled}
                  onCheckedChange={(v) => {
                    set('posts_types', { ...values.posts_types, [type]: { ...entry, enabled: v } })
                  }}
                />
                <span className={styles.typeText}>
                  <span className={styles.typeName}>{info.label}</span>
                  <span className={styles.hint}>{info.hint}</span>
                </span>
                <Segmented
                  size="sm"
                  aria-label={`Prioridade ${info.aria}`}
                  options={([1, 2, 3] as Priority[]).map((p) => ({
                    value: String(p),
                    label: PRIORITY_LABELS[p],
                    disabled: !entry.enabled,
                  }))}
                  value={String(entry.priority)}
                  onValueChange={(v) => {
                    set('posts_types', { ...values.posts_types, [type]: { ...entry, priority: Number(v) as Priority } })
                  }}
                />
              </div>
            )
          })}
        </div>
        {errors.posts_types ? (
          <p className={styles.error} role="alert">
            {errors.posts_types}
          </p>
        ) : null}
        <div className={styles.toggleHead}>
          <Toggle
            aria-label="Comemorar marcos de inscritos"
            checked={values.posts_milestone_enabled}
            onCheckedChange={(v) => {
              set('posts_milestone_enabled', v)
            }}
          />
          <div className={styles.toggleHeadText}>
            <span className={styles.labelRow}>
              <span className={styles.typeName}>Comemorar marcos de inscritos</span>
              <Chip>fora da cota semanal</Chip>
            </span>
            <span className={styles.hint}>Dispara quando o canal bate um marco de inscritos, em qualquer dia.</span>
          </div>
        </div>
      </Section>

      <Section title="Modo">
        <Field
          label="Modo de publicação"
          before={before('posts_require_approval')}
          hint={
            values.posts_require_approval
              ? 'Rascunho não aprovado até o horário do post vira Expirado e não é publicado.'
              : 'O post vai direto para a fila depois do filtro de segurança.'
          }
        >
          <Segmented
            aria-label="Modo de publicação"
            options={[
              { value: 'auto', label: 'Publicar automaticamente' },
              { value: 'approval', label: 'Exigir minha aprovação' },
            ]}
            value={values.posts_require_approval ? 'approval' : 'auto'}
            onValueChange={(v) => {
              set('posts_require_approval', v === 'approval')
            }}
          />
        </Field>
      </Section>

      <PostsQueue {...props} />
      <PostsPreview {...props} />

      <Section>
        <p className={styles.hint}>Todo texto passa pelo filtro de segurança antes de sair.</p>
        <p className={styles.hint}>O texto nunca se repete entre canais.</p>
      </Section>
    </div>
  )
}

function PostsQueue({ slug, posts, postsLoading, queueRef }: PostsTabProps) {
  const action = usePostAction(slug)
  const [editing, setEditing] = useState<number | null>(null)
  const [draft, setDraft] = useState('')
  // Cada linha tem o seu "ocupado" e o seu erro: aprovar B não pode liberar A.
  const [busy, setBusy] = useState<ReadonlySet<number>>(new Set())
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({})

  function run(input: Parameters<typeof action.mutateAsync>[0], onDone?: () => void) {
    const id = input.postId
    if (busy.has(id)) return
    setBusy((b) => new Set(b).add(id))
    setRowErrors(({ [id]: _drop, ...rest }) => rest)
    action
      .mutateAsync(input)
      .then(() => onDone?.())
      .catch((err: unknown) => {
        setRowErrors((e) => ({ ...e, [id]: errorMessage(err) }))
      })
      .finally(() => {
        setBusy((b) => {
          const next = new Set(b)
          next.delete(id)
          return next
        })
      })
  }

  return (
    <section className={styles.section} ref={queueRef} id="ch-posts-queue">
      <div className={styles.sectionHead}>
        <h3 className={styles.sectionTitle}>Fila e recentes</h3>
        <span className={styles.sectionMeta}>Aprovar, Editar e Rejeitar valem na hora — não dependem do Salvar.</span>
      </div>
      {postsLoading ? (
        <span className={styles.empty}>Carregando posts…</span>
      ) : !posts || posts.length === 0 ? (
        <span className={styles.empty}>Nenhum post na fila nem nos últimos 7 dias.</span>
      ) : (
        <div>
          <div className={styles.queueHead} aria-hidden>
            <span className={styles.label}>Tipo</span>
            <span className={styles.label}>Texto</span>
            <span className={styles.label}>Quando</span>
            <span className={styles.label}>Status</span>
          </div>
          {posts.map((post) => {
            const status = STATUS[post.status]
            const isDraft = post.status === 'draft'
            const isEditing = editing === post.id
            const rescheduled = post.rescheduled_from ?? []
            return (
              <div key={post.id} className={styles.queueRow}>
                <span>
                  <Chip>{post.type_label || post.type}</Chip>
                </span>
                <span
                  className={cn(styles.queueText, (post.status === 'expired' || post.status === 'rejected') && styles.queueMuted)}
                  title={post.text}
                >
                  {isEditing
                    ? 'Editando…'
                    : post.status === 'rejected'
                      ? 'Texto bloqueado — não foi publicado.'
                      : post.status === 'awaiting_video'
                        ? 'Aguardando o vídeo do dia para escrever o teaser.'
                        : post.text}
                </span>
                <span className={styles.queueWhen}>
                  {rescheduled.length ? <s>{whenLabel(rescheduled[rescheduled.length - 1])}</s> : null}
                  {whenLabel(post.when)}
                </span>
                <span className={styles.queueStatus}>
                  <Chip
                    variant={status.variant}
                    leadingIcon={post.status === 'rejected' ? <ShieldAlert /> : undefined}
                    title={post.detail || undefined}
                  >
                    {status.label}
                  </Chip>
                  {post.type === 'milestone' ? <Chip>fora da cota</Chip> : null}
                  {post.status === 'failed' ? (
                    <Link to="/jobs" className={styles.jobsLink}>
                      Ver em Jobs
                    </Link>
                  ) : null}
                </span>
                {isDraft && !isEditing ? (
                  <div className={styles.queueActions}>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy.has(post.id)}
                      onClick={() => {
                        run({ action: 'reject', postId: post.id })
                      }}
                    >
                      Rejeitar
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busy.has(post.id)}
                      onClick={() => {
                        setEditing(post.id)
                        setDraft(post.text)
                      }}
                    >
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      disabled={busy.has(post.id)}
                      onClick={() => {
                        run({ action: 'approve', postId: post.id })
                      }}
                    >
                      Aprovar
                    </Button>
                  </div>
                ) : null}
                {isEditing ? (
                  <div className={styles.queueEdit}>
                    <label className={styles.label} htmlFor={`ch-post-edit-${String(post.id)}`}>
                      Texto do post
                    </label>
                    <Textarea
                      id={`ch-post-edit-${String(post.id)}`}
                      value={draft}
                      maxLength={5000}
                      onChange={(e) => {
                        setDraft(e.target.value)
                      }}
                    />
                    <p className={styles.hint}>Ao salvar, o texto passa de novo pelo filtro de segurança.</p>
                    <div className={styles.queueEditActions}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditing(null)
                        }}
                      >
                        Cancelar edição
                      </Button>
                      <Button
                        size="sm"
                        leadingIcon={busy.has(post.id) ? <Spinner /> : undefined}
                        aria-disabled={busy.has(post.id)}
                        disabled={!draft.trim()}
                        onClick={() => {
                          run({ action: 'edit', postId: post.id, text: draft.trim() }, () => {
                            setEditing(null)
                          })
                        }}
                      >
                        {busy.has(post.id) ? 'Salvando…' : 'Salvar texto'}
                      </Button>
                    </div>
                  </div>
                ) : null}
                {rowErrors[post.id] ? (
                  <p className={cn(styles.error, styles.queueActions)} role="alert">
                    {rowErrors[post.id]}
                  </p>
                ) : null}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

function PostsPreview({ slug, values, errors, session }: PostsTabProps) {
  const preview = usePreviewPosts(slug)
  const [generatedAt, setGeneratedAt] = useState<Date | null>(null)
  const perWeek = postsPerWeekOf(values.posts_days)
  const count = Math.min(Math.max(perWeek, 1), 2)
  const items: PostPreviewOut[] = preview.data ?? []
  // A prévia usa a config da tela: com ela inválida, a API responderia 422. Sem dia
  // marcado também, mesmo com o toggle desligado: a prévia liga os posts no backend.
  const blocked =
    values.posts_days.length === 0 || Boolean(errors.posts_days || errors.posts_types || errors.posts_time)

  function generate() {
    if (preview.isPending || blocked) return
    preview.mutate(
      {
        posts_per_week: perWeek,
        posts_days: values.posts_days,
        posts_time: values.posts_time_mode === 'fixed' ? values.posts_time : null,
        posts_types: typeWeights(values.posts_types),
        count,
      },
      {
        onSuccess: () => {
          setGeneratedAt(new Date())
        },
      },
    )
  }

  return (
    <section className={styles.section} aria-busy={preview.isPending}>
      <div className={styles.sectionHead}>
        <h3 className={styles.sectionTitle}>Prévia</h3>
        <Chip>não publica</Chip>
        <span className={styles.sectionMeta}>
          {preview.isPending
            ? `gerando ${String(count)} posts…`
            : generatedAt
              ? `gerada hoje ${clockLabel(generatedAt.toISOString())}`
              : null}
        </span>
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={preview.isPending ? <Spinner /> : <Sparkles />}
          aria-disabled={preview.isPending}
          disabled={blocked}
          title={blocked ? 'Corrija a frequência e os tipos para gerar a prévia' : undefined}
          onClick={generate}
        >
          {preview.isPending ? 'Gerando…' : 'Gerar prévia'}
        </Button>
      </div>
      <p className={styles.hint}>
        Mostra como seriam os próximos posts com a configuração atual, antes de salvar. Nada daqui vai para a fila.
      </p>
      {preview.isPending ? (
        <div>
          {Array.from({ length: count }, (_, i) => (
            <div key={i} className={styles.previewRow}>
              <span className={styles.skeletonDay}>
                <Skeleton width={72} />
                <Skeleton width={96} height={8} />
              </span>
              <Skeleton width={70} height={20} round />
              <Skeleton width={i === 0 ? '88%' : '64%'} />
            </div>
          ))}
        </div>
      ) : items.length ? (
        <div>
          {items.map((item) => (
            <div key={item.slot_at} className={styles.previewRow}>
              <span className={styles.previewDay}>
                <span>{dayLabel(item.slot_at)}</span>
                <span>{item.best_time ? 'melhor horário' : clockLabel(item.slot_at)}</span>
              </span>
              <span>
                <Chip>{item.type_label}</Chip>
              </span>
              <span className={cn(item.error && styles.queueMuted)}>
                {item.error ? item.error : item.text}
                {item.options?.length ? ` ${item.options.map((o, i) => `${String.fromCharCode(65 + i)}) ${o}`).join(' · ')}` : ''}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {preview.isError ? (
        <p className={styles.error} role="alert">
          {errorMessage(preview.error)}
        </p>
      ) : null}
      {session?.state === 'expired' ? (
        <p className={styles.hint}>A prévia funciona com a sessão expirada — ela só usa IA.</p>
      ) : null}
    </section>
  )
}
